import { BlackBoxCard, HiddenPostForm, IBlackBoxCMS } from '@vfit/consumer/components';
import { useQueryClient } from 'react-query';
import {
  CONSUMER_CMS_SHOPPING_CART,
  convertMbToGb,
  getBestTechFromCoverage,
  IGlobalConfigCMS,
  isHiddenVfFwFrontend,
  isTargetBlankVfFw,
  organizeBestTechFromServiceAbility,
  retrieveProductTechnology,
  useCmsConfig,
} from '@vfit/consumer/data-access';
import { useState } from 'react';
import { IProposalCard, offersProposalManager } from '@vfit/consumer/providers';
import { useCheckout } from '../../../iBuyFixed.context';
import { API, errorManager, ErrorService, ITrackView } from '@vfit/shared/data-access';
import { trackProposal } from './cartOfferProposal.utils';

const CartOfferProposal = () => {
  const { product, setIsShowCheckout } = useCheckout();
  const queryClient = useQueryClient();
  const cmsGlobalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG,
  ) as IGlobalConfigCMS;
  const { blackbox: blackboxFromCMS } = (queryClient?.getQueryData('getBestOfferVfFw') ||
    {}) as IBlackBoxCMS;
  const bestTech = organizeBestTechFromServiceAbility();
  const bestTechFromCoverage = getBestTechFromCoverage(queryClient);
  const isHiddenFE = isHiddenVfFwFrontend(product, cmsGlobalConfig);
  const fwPostTarget = isTargetBlankVfFw(product, cmsGlobalConfig);
  const productTechnology = retrieveProductTechnology();
  const trackObj: ITrackView = {
    event_name: 'cart_view',
    event_label_track: bestTech?.bestTechValue,
    event_category: 'sales',
    page_type: 'cart pop up',
    journey_type: 'checkout',
    tracking_type: 'view',
    journey_name: 'checkout',
    page_section: 'fixed offers',
    page_subsection: 'checkout',
    ...(productTechnology && {
      product_technology: `${productTechnology}_${bestTechFromCoverage?.fastwebTechnology?.mapping?.displayTechnology}`,
    }),
  };
  const [postForm, setPostForm] = useState<{
    isEnabled: boolean;
    url: string;
    params: {
      [key: string]: {
        name: string;
        value: string;
      };
    };
    target?: string;
  }>({
    isEnabled: false,
    url: process?.env?.['NEXT_PUBLIC_FASTWEB_POST_COVERAGE'] || '',
    params: {},
    target: fwPostTarget,
  });

  const getPostFastweb = () => {
    if (!bestTech?.checkSum || !bestTech?.eagonId) {
      errorManager.handleError(ErrorService.getSeverityErrorMedium(), {
        isButtonSticky: true,
        title: 'Ops',
        message: 'Si è verificato un errore, riprova in seguito.',
        actionText: 'Riprova',
        actionEvent: () => {
          window.location.reload();
        },
        disableTrack: true,
      });
      return;
    }
    setPostForm({
      ...postForm,
      isEnabled: true,
      target: fwPostTarget,
      params: {
        eagonId: {
          name: 'idCivicoEgon',
          value: bestTech?.eagonId,
        },
        checksum: {
          name: 'chk',
          value: bestTech?.checkSum || '',
        },
        ...(productTechnology && {
          techVf: {
            name: 'techVF',
            value: productTechnology,
          },
        }),
        ...(bestTech && {
          bestTech: {
            name: 'bestTech',
            value: bestTech.bestTechValue || '',
          },
        }),
      },
    });
  };

  const showFrontendWithProposal = () => {
    const getCardAction = (card?: IProposalCard) => {
      if (!card) return;
      if (card?.isVodafone) {
        setIsShowCheckout(true);
        return;
      }
      getPostFastweb();
      if (fwPostTarget) {
        setTimeout(
          () =>
            setPostForm({
              ...postForm,
              isEnabled: false,
            }),
          800,
        );
      }
    };
    offersProposalManager.handleOffersProposal({
      showModal: true,
      ...bestTechFromCoverage,
      firstCardAction: {
        ...bestTechFromCoverage?.firstCardAction,
        ...(bestTechFromCoverage?.firstCard?.isVodafone && { enableCloseOnClickAction: true }),
        action: () => getCardAction(bestTechFromCoverage?.firstCard),
      },
      secondCardAction: {
        ...bestTechFromCoverage?.secondCardAction,
        ...(bestTechFromCoverage?.secondCard?.isVodafone && { enableCloseOnClickAction: true }),
        action: () => getCardAction(bestTechFromCoverage?.secondCard),
      },
    });
  };

  const showProposal = () => {
    if (blackboxFromCMS?.enableSplittedOfferView === 'true') {
      trackProposal(queryClient, product);
      return showFrontendWithProposal();
    }
    getPostFastweb();
    if (fwPostTarget) {
      setTimeout(
        () =>
          setPostForm({
            ...postForm,
            isEnabled: false,
          }),
        800,
      );
    }
  };

  const getBlackboxConfiguration = () => {
    const removeBBPlacehodler = (label?: string) => {
      return label
        ?.replace(
          '$FWTECH',
          bestTechFromCoverage?.fastwebTechnology?.mapping?.displayTechnology || '',
        )
        ?.replace('$MAXDOWNLOAD', convertMbToGb(bestTech?.bestTechMaxDownloadSpeed || ''))
        ?.replace('$MAXUPLOAD', convertMbToGb(bestTech?.bestTechMaxUploadSpeed || ''));
    };
    return {
      ...blackboxFromCMS,
      showX: blackboxFromCMS?.showX === 'true',
      title: removeBBPlacehodler(
        bestTechFromCoverage?.blackboxTitle || blackboxFromCMS?.title || '',
      ),
      liteTitle: removeBBPlacehodler(
        bestTechFromCoverage?.blackboxLiteTitle ||
          blackboxFromCMS?.liteTitle ||
          'Vuoi più potenza?',
      ),
      description: removeBBPlacehodler(
        bestTechFromCoverage?.blackboxDescription || blackboxFromCMS?.description || '',
      ),
    };
  };

  if (!bestTechFromCoverage?.showBlackBox || isHiddenFE) return;
  return (
    <>
      <BlackBoxCard
        blackBoxConfig={getBlackboxConfiguration()}
        onClickButton={showProposal}
        trackObj={trackObj}
      />
      {postForm?.isEnabled && (
        <HiddenPostForm
          url={postForm?.url || ''}
          params={postForm?.params}
          target={postForm?.target}
        />
      )}
    </>
  );
};

export default CartOfferProposal;

